import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { ShoppingCart, User, SignOut, List, Storefront, Package, ChartLine, Gear } from "@phosphor-icons/react";

export default function Navigation() {
  const { user, logout } = useAuth();
  const { totalCount } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    setProfileOpen(false);
    navigate("/");
  };

  return (
    <header
      data-testid="main-navigation"
      className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-[#F9F7F3]/85 border-b border-[#E5E2D9]/60 transition-all"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <img
            src="/afripantry-logo.png"
            alt="AfriPantry"
            className="h-7 md:h-8 w-auto"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          <NavLink
            to="/"
            end
            data-testid="nav-marketplace"
            className={({ isActive }) =>
              `px-4 py-2 rounded-full text-sm font-semibold transition-all ${isActive ? "bg-[#F2EFE9] text-[#1A1A18]" : "text-[#5C5C54] hover:text-[#1A1A18] hover:bg-[#F2EFE9]"}`
            }
          >
            Marketplace
          </NavLink>
          <NavLink
            to="/vendors"
            data-testid="nav-vendors"
            className={({ isActive }) =>
              `px-4 py-2 rounded-full text-sm font-semibold transition-all ${isActive ? "bg-[#F2EFE9] text-[#1A1A18]" : "text-[#5C5C54] hover:text-[#1A1A18] hover:bg-[#F2EFE9]"}`
            }
          >
            Vendors
          </NavLink>
          {user && (
            <NavLink
              to="/orders"
              data-testid="nav-orders"
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-semibold transition-all ${isActive ? "bg-[#F2EFE9] text-[#1A1A18]" : "text-[#5C5C54] hover:text-[#1A1A18] hover:bg-[#F2EFE9]"}`
              }
            >
              My Orders
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/cart"
            data-testid="nav-cart-link"
            className="relative p-2 rounded-full hover:bg-[#F2EFE9] transition-all"
          >
            <ShoppingCart size={22} weight="duotone" className="text-[#1A1A18]" />
            {totalCount > 0 && (
              <span
                data-testid="cart-badge-count"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#A94438] text-white text-[10px] font-bold flex items-center justify-center"
              >
                {totalCount}
              </span>
            )}
          </Link>

          {user ? (
            <div className="relative">
              <button
                data-testid="profile-menu-btn"
                onClick={() => setProfileOpen(!profileOpen)}
                className="w-9 h-9 rounded-full bg-[#F2EFE9] border border-[#E5E2D9] flex items-center justify-center hover:border-[#D1CEC5] transition-all"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span className="font-heading font-bold text-sm text-[#A94438]">{(user.name || user.email)[0].toUpperCase()}</span>
                )}
              </button>
              {profileOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-[#E5E2D9] rounded-2xl shadow-[0_20px_40px_rgb(0,0,0,0.08)] py-2 z-50">
                  <div className="px-4 py-3 border-b border-[#E5E2D9]">
                    <p className="font-semibold text-sm truncate">{user.name}</p>
                    <p className="text-xs text-[#5C5C54] truncate">{user.email}</p>
                    <p className="text-xs mt-1 chip">{user.role}</p>
                  </div>
                  <Link
                    to="/profile"
                    onClick={() => setProfileOpen(false)}
                    data-testid="profile-menu-profile"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium hover:bg-[#F2EFE9] transition-all"
                  >
                    <User size={18} weight="duotone" />
                    Profile
                  </Link>
                  <Link
                    to="/orders"
                    onClick={() => setProfileOpen(false)}
                    data-testid="profile-menu-orders"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium hover:bg-[#F2EFE9] transition-all"
                  >
                    <Package size={18} weight="duotone" />
                    My Orders
                  </Link>
                  {(user.role === "vendor" || user.role === "admin") && (
                    <Link
                      to="/vendor"
                      onClick={() => setProfileOpen(false)}
                      data-testid="profile-menu-vendor"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium hover:bg-[#F2EFE9] transition-all"
                    >
                      <Storefront size={18} weight="duotone" />
                      Vendor Dashboard
                    </Link>
                  )}
                  {user.role !== "vendor" && user.role !== "admin" && (
                    <Link
                      to="/become-vendor"
                      onClick={() => setProfileOpen(false)}
                      data-testid="profile-menu-become-vendor"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium hover:bg-[#F2EFE9] transition-all"
                    >
                      <Storefront size={18} weight="duotone" />
                      Become a Vendor
                    </Link>
                  )}
                  {user.role === "admin" && (
                    <Link
                      to="/admin"
                      onClick={() => setProfileOpen(false)}
                      data-testid="profile-menu-admin"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium hover:bg-[#F2EFE9] transition-all"
                    >
                      <ChartLine size={18} weight="duotone" />
                      Admin Panel
                    </Link>
                  )}
                  <div className="border-t border-[#E5E2D9] mt-1 pt-1">
                    <button
                      onClick={handleLogout}
                      data-testid="profile-menu-logout"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium w-full text-left hover:bg-[#F2EFE9] text-[#A94438] transition-all"
                    >
                      <SignOut size={18} weight="duotone" />
                      Log out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login-link" className="btn-primary text-xs px-5 py-2.5">
              Sign in
            </Link>
          )}

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-btn"
            className="md:hidden p-2 rounded-full hover:bg-[#F2EFE9]"
          >
            <List size={22} weight="duotone" />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-[#E5E2D9] bg-[#F9F7F3]/95 backdrop-blur-xl">
          <nav className="px-4 py-3 flex flex-col gap-1">
            <NavLink to="/" end onClick={() => setMenuOpen(false)} className="px-4 py-3 rounded-xl text-sm font-semibold hover:bg-[#F2EFE9]">Marketplace</NavLink>
            <NavLink to="/vendors" onClick={() => setMenuOpen(false)} className="px-4 py-3 rounded-xl text-sm font-semibold hover:bg-[#F2EFE9]">Vendors</NavLink>
            {user && (
              <NavLink to="/orders" onClick={() => setMenuOpen(false)} className="px-4 py-3 rounded-xl text-sm font-semibold hover:bg-[#F2EFE9]">My Orders</NavLink>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
